import * as React from 'react'
import { connect } from 'react-redux'
import withQueryParams, { InjectedQueryParams } from 'react-router-query-params'

import Moment from 'react-moment'
import Pagination from 'react-js-pagination'
import { Table, Collapse } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { RootState } from '../../store/rootReducer'
import { QACheck } from '../../lib/interfaces/QACheck.interface'
import { Location } from '../../lib/interfaces/Location.interface'
import { BaseReportFilterParameters } from '../../lib/interfaces/BaseReportFilterParameters.interface'

import * as qaChecksActions from '../../store/qaChecks/actions'

import Breadcrumbs from '../Breadcrumbs.component'
import QAChecksFilter from './components/QAChecksFilter.component'

import * as authSelectors from 'src/store/auth/selectors'

import { numberArrayDefaultValue } from '../../utils/query-params'

const get = require('lodash/get')
const map = require('lodash/map')
const size = require('lodash/size')
const first = require('lodash/first')
const slice = require('lodash/slice')
const isEmpty = require('lodash/isEmpty')

const moment = require('moment')

const PAGE_SIZE = 5000

interface QAChecksListProps extends InjectedQueryParams<BaseReportFilterParameters> {
  qaChecks?: QACheck[]
  isFetchingQAChecks: boolean
  principalLocation?: Location
  filterParams: BaseReportFilterParameters
  setCurrentQACheck: (qaCheck: QACheck) => void
  setFilterParams: (filterParams: BaseReportFilterParameters) => void
  fetchQAChecksList: (next?: (err?: any, qaChecks?: QACheck[]) => void) => void
}

interface QAChecksListState {
  currentPage: number
}

class QAChecksListPage extends React.PureComponent<QAChecksListProps, QAChecksListState> {
  state: QAChecksListState = {
    currentPage: 0
  }

  componentDidMount() {
    const { queryParams, setFilterParams, fetchQAChecksList } = this.props
    
    setFilterParams(queryParams)

    if (queryParams.location !== undefined) {
      fetchQAChecksList()
    }
  }

  onFiltersChange = (filters: BaseReportFilterParameters) => {
    this.props.setFilterParams(filters)
    this.props.setQueryParams(filters)
    this.props.fetchQAChecksList()

    this.setState({ currentPage: 0 })
  }

  get qaChecksToShow() {
    const { qaChecks } = this.props
    const { currentPage } = this.state

    const initialIndex = currentPage * PAGE_SIZE
    return slice(qaChecks, initialIndex, initialIndex + PAGE_SIZE)
  }

  renderPagination() {
    const { qaChecks } = this.props
    const { currentPage } = this.state

    const arraySize = size(qaChecks)
    if (arraySize <= PAGE_SIZE) {
      return null
    }

    return (
      <div className="d-flex justify-content-end">
        <Pagination
          itemClass="page-item"
          linkClass="page-link"
          totalItemsCount={arraySize}
          activePage={currentPage + 1}
          itemsCountPerPage={PAGE_SIZE}
          onChange={page => this.setState({ currentPage: page - 1 })}
        />
      </div>
    )
  }

  render() {
    const { queryParams, setCurrentQACheck, isFetchingQAChecks } = this.props

    return (
      <div className="d-flex flex-column min-height-100">
        <Breadcrumbs breadcrumbs={[
          { name: 'Dashboard', link: '/dashboard' },
          { name: 'QA', active: true },
        ]} />

        <div className="page applications-page flex-grow-1 min-height-0">
          <section>
            <div className="d-flex align-items-center mb-3">
              <h5 className="title mb-0">QA Checks</h5>
              {isFetchingQAChecks && <FontAwesomeIcon spin size="lg" className="ml-3" icon={['fas', 'spinner']} />}
            </div>

            <QAChecksFilter
              filterParams={queryParams}
              onChange={this.onFiltersChange}
            />

            <Table hover className="ggtable">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>ID</th>
                  <th style={{ width: '20%' }}>Time</th>
                  <th style={{ width: '25%' }}>Position</th>
                  <th style={{ width: '35%' }}>VIN</th>
                </tr>
              </thead>
              
              <tbody>
                {map(this.qaChecksToShow, qaCheck => (
                  <tr key={qaCheck.id} onClick={() => setCurrentQACheck(qaCheck)}>
                    <td>{qaCheck.driverId}</td>
                    <td><Moment date={qaCheck.timeScanned} format="hh:mm:ss A" /></td>
                    <td>{get(qaCheck, 'position.name', '-')}</td>
                    <td>{qaCheck.vin || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {this.renderPagination()}
          </section>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  qaChecks: state.qaChecks.qaChecks,
  filterParams: state.qaChecks.filterParams,
  isFetchingQAChecks: state.qaChecks.isFetchingQAChecks,
  principalLocation: authSelectors.getPrincipalLocation(state)
})

const mapDispatchToProps = (dispatch) => ({
  setCurrentQACheck: (qaCheck: QACheck) => dispatch(qaChecksActions.setCurrentQACheck(qaCheck)),
  setFilterParams: (filterParams: BaseReportFilterParameters) =>
    dispatch(qaChecksActions.setQAChecksFilterParams(filterParams)),
  fetchQAChecksList: (next?: (err?: any, qaChecks?: QACheck[]) => void) =>
    dispatch(qaChecksActions.fetchQAChecksList(next))
})

export default connect(mapStateToProps, mapDispatchToProps)(
  withQueryParams<BaseReportFilterParameters, QAChecksListProps>({
    stripUnknownKeys: true,
    keys: {
      location: {
        validate: () => false,
        default: (value, props) => {
          if (value) { return first(numberArrayDefaultValue(value)) }

          if (props.filterParams.location !== undefined) {
            return props.filterParams.location
          }
          
          if (props.principalLocation !== undefined && props.principalLocation.code !== 'MCO') {
            return props.principalLocation.id
          }

          return undefined
        }
      },
      position: {
        validate: () => false,
        default: (value, props) => {
          if (value) { return numberArrayDefaultValue(value) }
          return props.filterParams.position
        }
      },
      fromDate: {
        default: (value, props) => props.filterParams.fromDate,
        validate: value => {
          if (!value) { return false }
          return moment(value).isValid()
        }
      }
    }
  }
)(QAChecksListPage))


// WEBPACK FOOTER //
// ./src/routes/qa-checks/QAChecksList.page.tsx